import React, { useState, useRef } from 'react';
import {
  Upload,
  Split,
  RotateCw,
  RotateCcw,
  Trash2,
  Download,
  ShieldCheck,
  Plus,
  Layers,
  CheckSquare,
  Square,
  X,
  ArrowUpDown,
  FilePlus,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../shared/components/Button';
import { Card } from '../../shared/components/Card';
import { useToast } from '../../shared/context/ToastContext';
import { StudioPage } from './types';
import {
  parsePdfToStudioPages,
  parseAstToStudioPages,
  compileStudioPagesToPdf,
  createBlankStudioPage,
} from './utils/pdfProcessor';
import { PageThumbnailCard } from './components/PageThumbnailCard';
import { ImportSourceModal } from './components/ImportSourceModal';
import { ExtractPagesModal } from './components/ExtractPagesModal';
import { downloadPdfBytes } from '../../core/export/pdfExporter';
import { sanitizeFilename } from '../../core/export/documentValidator';
import { DocumentModel } from '../../core/ast/document.types';

export const DocumentStudio: React.FC = () => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Core State
  const [pages, setPages] = useState<StudioPage[]>([]);
  const [selectedPageIds, setSelectedPageIds] = useState<Set<string>>(new Set());
  const [documentTitle, setDocumentTitle] = useState('Final_Combined_Document');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Modals & Viewer State
  const [showImportSourceModal, setShowImportSourceModal] = useState(false);
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [previewPageIndex, setPreviewPageIndex] = useState<number | null>(null);
  const [previewZoom, setPreviewZoom] = useState<number>(100);

  // File Upload Handler
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsProcessing(true);
    let addedCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        showToast(`Skipped "${file.name}" — Only PDF files are supported.`, 'warning');
        continue;
      }

      if (file.size > 50 * 1024 * 1024) {
        showToast(`Skipped "${file.name}" — PDF file exceeds 50MB limit.`, 'error');
        continue;
      }

      try {
        const buffer = await file.arrayBuffer();
        const uint8Bytes = new Uint8Array(buffer);
        const sourceId = `doc-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
        const parsedPages = await parsePdfToStudioPages(uint8Bytes, file.name, sourceId);

        setPages((prev) => [...prev, ...parsedPages]);
        addedCount += parsedPages.length;
      } catch (err) {
        console.error('Failed to parse PDF:', err);
        showToast(`Failed to parse "${file.name}". Ensure the PDF is not password protected.`, 'error');
      }
    }

    setIsProcessing(false);
    if (addedCount > 0) {
      showToast(`Imported ${addedCount} pages successfully.`, 'success');
    }
  };

  // Import from saved Studio AST Document
  const handleImportAstDoc = async (doc: DocumentModel) => {
    setIsProcessing(true);
    try {
      const parsedPages = await parseAstToStudioPages(doc);
      setPages((prev) => [...prev, ...parsedPages]);
      showToast(`Added ${parsedPages.length} pages from "${doc.metadata.title}".`, 'success');
    } catch (err) {
      console.error('Failed to import saved document:', err);
      showToast('Error importing saved document pages.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Add Blank Page
  const handleAddBlankPage = async () => {
    try {
      const blankPage = await createBlankStudioPage(pages.length + 1);
      setPages((prev) => [...prev, blankPage]);
      showToast('Added a new blank A4 page.', 'info');
    } catch (err) {
      console.error('Failed to add blank page:', err);
    }
  };

  // Selection Handlers
  const togglePageSelect = (pageId: string) => {
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedPageIds(new Set(pages.map((p) => p.id)));
  };

  const deselectAll = () => {
    setSelectedPageIds(new Set());
  };

  const invertSelection = () => {
    setSelectedPageIds((prev) => {
      const next = new Set<string>();
      pages.forEach((p) => {
        if (!prev.has(p.id)) next.add(p.id);
      });
      return next;
    });
  };

  // Page Operations & Reordering
  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= pages.length || toIndex >= pages.length) return;
    setPages((prev) => {
      const next = [...prev];
      const [movedItem] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, movedItem);
      return next;
    });
  };

  const handleMoveLeft = (index: number) => {
    if (index <= 0) return;
    handleReorder(index, index - 1);
  };

  const handleMoveRight = (index: number) => {
    if (index >= pages.length - 1) return;
    handleReorder(index, index + 1);
  };

  const handleMoveToFirst = (index: number) => {
    if (index === 0) return;
    handleReorder(index, 0);
  };

  const handleMoveToLast = (index: number) => {
    if (index === pages.length - 1) return;
    handleReorder(index, pages.length - 1);
  };

  const handleReverseOrder = () => {
    if (pages.length <= 1) return;
    setPages((prev) => [...prev].reverse());
    showToast('Reversed page order.', 'info');
  };

  const handleRotateCw = (pageId: string) => {
    setPages((prev) =>
      prev.map((p) => (p.id === pageId ? { ...p, rotation: (p.rotation + 90) % 360 } : p))
    );
  };

  const handleRotateCcw = (pageId: string) => {
    setPages((prev) =>
      prev.map((p) => (p.id === pageId ? { ...p, rotation: (p.rotation + 270) % 360 } : p))
    );
  };

  const handleRotateSelected = (delta: number) => {
    if (selectedPageIds.size === 0) return;
    setPages((prev) =>
      prev.map((p) =>
        selectedPageIds.has(p.id) ? { ...p, rotation: (p.rotation + delta + 360) % 360 } : p
      )
    );
    showToast(`Rotated ${selectedPageIds.size} selected pages.`, 'info');
  };

  const handleDuplicatePage = (pageId: string) => {
    const targetIdx = pages.findIndex((p) => p.id === pageId);
    if (targetIdx === -1) return;
    const target = pages[targetIdx];
    const clone: StudioPage = {
      ...target,
      id: `p-${Date.now().toString(36)}-clone-${Math.random().toString(36).substring(2, 6)}`,
    };
    setPages((prev) => {
      const next = [...prev];
      next.splice(targetIdx + 1, 0, clone);
      return next;
    });
    showToast('Page duplicated.', 'info');
  };

  const handleDeletePage = (pageId: string) => {
    setPages((prev) => prev.filter((p) => p.id !== pageId));
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      next.delete(pageId);
      return next;
    });
    showToast('Page removed.', 'info');
  };

  const handleDeleteSelected = () => {
    if (selectedPageIds.size === 0) return;
    const count = selectedPageIds.size;
    setPages((prev) => prev.filter((p) => !selectedPageIds.has(p.id)));
    setSelectedPageIds(new Set());
    showToast(`Removed ${count} pages.`, 'info');
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all pages from Document Studio?')) {
      setPages([]);
      setSelectedPageIds(new Set());
      showToast('Workspace cleared.', 'info');
    }
  };

  // Export Combined Document
  const handleExportFinalPdf = async () => {
    if (pages.length === 0) return;
    setIsExporting(true);
    try {
      const compiledBytes = await compileStudioPagesToPdf(pages);
      downloadPdfBytes(compiledBytes, sanitizeFilename(documentTitle, 'pdf'));
      showToast(`Exported ${pages.length} pages to "${sanitizeFilename(documentTitle, 'pdf')}"!`, 'success');
    } catch (err) {
      console.error('Failed to compile PDF:', err);
      showToast('Error assembling final PDF. Please check page sources.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const selectedPagesList = pages.filter((p) => selectedPageIds.has(p.id));
  const activePreviewPage = previewPageIndex !== null ? pages[previewPageIndex] : null;

  return (
    <div
      style={{
        padding: 'var(--space-6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-6)',
        maxWidth: '1360px',
        margin: '0 auto',
        width: '100%',
      }}
    >
      {/* Top Header & Export Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-4)',
          backgroundColor: 'var(--bg-surface)',
          padding: 'var(--space-4) var(--space-6)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>
              Document & Page Studio
            </h1>
            <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <ShieldCheck size={12} /> 100% Client-Side
            </span>
          </div>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: '2px' }}>
            Combine, reorder, drag & drop, rotate, split, and merge academic PDF pages in high-fidelity vector format.
          </p>
        </div>

        {/* Action Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(e.target.files)}
            multiple
            accept="application/pdf"
            style={{ display: 'none' }}
          />

          <Button
            variant="secondary"
            size="sm"
            icon={<Upload size={14} />}
            onClick={() => fileInputRef.current?.click()}
            loading={isProcessing}
          >
            Upload PDF
          </Button>

          <Button
            variant="secondary"
            size="sm"
            icon={<Layers size={14} />}
            onClick={() => setShowImportSourceModal(true)}
          >
            Insert Saved Doc
          </Button>

          <Button
            variant="secondary"
            size="sm"
            icon={<FilePlus size={14} />}
            onClick={handleAddBlankPage}
            title="Add a blank A4 page"
          >
            Add Blank Page
          </Button>

          <div style={{ height: '24px', width: '1px', backgroundColor: 'var(--border-default)' }} />

          {/* Export / Combine Bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input
              type="text"
              className="input"
              value={documentTitle}
              onChange={(e) => setDocumentTitle(e.target.value)}
              placeholder="Output Document Name"
              style={{
                width: '200px',
                height: '34px',
                fontSize: 'var(--font-size-xs)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
              }}
              title="Filename before export"
            />

            <Button
              variant="primary"
              size="sm"
              icon={<Download size={14} />}
              onClick={handleExportFinalPdf}
              disabled={pages.length === 0}
              loading={isExporting}
            >
              Merge & Export PDF ({pages.length})
            </Button>
          </div>
        </div>
      </div>

      {/* Main Workspace Area */}
      {pages.length === 0 ? (
        /* Empty State / Initial Drop Zone */
        <Card
          style={{
            border: isDraggingOver ? '2px dashed var(--primary-500)' : '2px dashed var(--border-default)',
            backgroundColor: isDraggingOver ? 'var(--primary-50)' : 'var(--bg-surface)',
            padding: 'var(--space-16) var(--space-8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            minHeight: '380px',
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDraggingOver(true);
          }}
          onDragLeave={() => setIsDraggingOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingOver(false);
            handleFileUpload(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: 'var(--radius-xl)',
              backgroundColor: 'var(--primary-50)',
              color: 'var(--primary-600)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--space-4)',
            }}
          >
            <Upload size={36} />
          </div>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
            Drop PDF files here, or click to browse
          </h2>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', maxWidth: '460px', marginBottom: 'var(--space-6)' }}>
            Upload lab reports, scanned circuit schematics, homework problem sets, or insert generated cover pages to reorder, split, and merge.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="primary"
              icon={<Upload size={14} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose PDF File
            </Button>
            <Button
              variant="secondary"
              icon={<Layers size={14} />}
              onClick={() => setShowImportSourceModal(true)}
            >
              Insert Saved Report / Cover
            </Button>
            <Button
              variant="secondary"
              icon={<FilePlus size={14} />}
              onClick={handleAddBlankPage}
            >
              Add Blank Page
            </Button>
          </div>
        </Card>
      ) : (
        /* Active Multi-Page Grid & Toolbar */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {/* Quick Page Actions Toolbar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-2) var(--space-4)',
              backgroundColor: 'var(--bg-surface)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              flexWrap: 'wrap',
              gap: 'var(--space-2)',
            }}
          >
            {/* Selection Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              {selectedPageIds.size === pages.length ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)' }}
                  onClick={deselectAll}
                >
                  <CheckSquare size={14} color="var(--primary-600)" /> Deselect All
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-xs)' }}
                  onClick={selectAll}
                >
                  <Square size={14} /> Select All ({pages.length})
                </button>
              )}

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 'var(--font-size-xs)' }}
                onClick={invertSelection}
                title="Invert current selection"
              >
                Invert
              </button>

              {selectedPageIds.size > 0 && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--primary-600)', fontWeight: 600, marginLeft: 'var(--space-1)' }}>
                  {selectedPageIds.size} of {pages.length} selected
                </span>
              )}
            </div>

            {/* Batch Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 'var(--font-size-xs)' }}
                onClick={handleReverseOrder}
                title="Reverse all page order (fixes upside down/backwards scans)"
              >
                <ArrowUpDown size={13} style={{ marginRight: '4px' }} /> Reverse Order
              </button>

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 'var(--font-size-xs)' }}
                disabled={selectedPageIds.size === 0}
                onClick={() => handleRotateSelected(-90)}
                title="Rotate Selected CCW"
              >
                <RotateCcw size={13} style={{ marginRight: '4px' }} /> Rotate CCW
              </button>

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 'var(--font-size-xs)' }}
                disabled={selectedPageIds.size === 0}
                onClick={() => handleRotateSelected(90)}
                title="Rotate Selected CW"
              >
                <RotateCw size={13} style={{ marginRight: '4px' }} /> Rotate CW
              </button>

              {/* Split & Extract Modal Button */}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ fontSize: 'var(--font-size-xs)' }}
                onClick={() => setShowExtractModal(true)}
                title="Split document into separate PDFs or extract page ranges"
              >
                <Split size={13} style={{ marginRight: '4px' }} /> Split & Extract
              </button>

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                disabled={selectedPageIds.size === 0}
                onClick={handleDeleteSelected}
                title="Delete Selected Pages"
              >
                <Trash2 size={13} style={{ marginRight: '4px' }} /> Delete ({selectedPageIds.size})
              </button>

              <div style={{ height: '18px', width: '1px', backgroundColor: 'var(--border-default)', margin: '0 4px' }} />

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                onClick={handleClearAll}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Drag & Drop Reorder Page Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 'var(--space-4)',
            }}
          >
            {pages.map((page, idx) => (
              <PageThumbnailCard
                key={page.id}
                page={page}
                index={idx}
                totalCount={pages.length}
                isSelected={selectedPageIds.has(page.id)}
                onToggleSelect={togglePageSelect}
                onRotateCw={handleRotateCw}
                onRotateCcw={handleRotateCcw}
                onMoveLeft={handleMoveLeft}
                onMoveRight={handleMoveRight}
                onMoveToFirst={handleMoveToFirst}
                onMoveToLast={handleMoveToLast}
                onReorder={handleReorder}
                onDuplicate={handleDuplicatePage}
                onDelete={handleDeletePage}
                onPreview={() => {
                  setPreviewPageIndex(idx);
                  setPreviewZoom(100);
                }}
              />
            ))}

            {/* Add More Pages Card */}
            <div
              style={{
                border: '2px dashed var(--border-default)',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'var(--bg-surface-subtle)',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                gap: 'var(--space-2)',
                color: 'var(--text-muted)',
                transition: 'border-color 0.15s ease',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Plus size={20} color="var(--primary-600)" />
              </div>
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>Add PDF Files</span>
              <span style={{ fontSize: 'var(--font-size-xs)' }}>or drag & drop here</span>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ImportSourceModal
        isOpen={showImportSourceModal}
        onClose={() => setShowImportSourceModal(false)}
        onSelectDoc={handleImportAstDoc}
      />

      <ExtractPagesModal
        isOpen={showExtractModal}
        onClose={() => setShowExtractModal(false)}
        selectedPages={selectedPagesList}
        allPages={pages}
      />

      {/* Interactive High-Res Page Zoom Preview Modal */}
      {previewPageIndex !== null && activePreviewPage && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(8px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)',
          }}
          onClick={() => setPreviewPageIndex(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-2xl)',
              maxWidth: '850px',
              width: '100%',
              height: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid var(--border-subtle)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Viewer Header */}
            <div
              style={{
                padding: 'var(--space-3) var(--space-5)',
                borderBottom: '1px solid var(--border-subtle)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'var(--bg-surface-subtle)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700 }}>
                  Page {previewPageIndex + 1} of {pages.length}
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  Source: {activePreviewPage.sourceDocName} ({activePreviewPage.rotation}°)
                </span>
              </div>

              {/* Viewer Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setPreviewZoom((z) => Math.max(50, z - 25))}
                  title="Zoom Out"
                >
                  <ZoomOut size={16} />
                </button>

                <span style={{ fontSize: '11px', fontWeight: 600, minWidth: '38px', textAlign: 'center' }}>
                  {previewZoom}%
                </span>

                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setPreviewZoom((z) => Math.min(200, z + 25))}
                  title="Zoom In"
                >
                  <ZoomIn size={16} />
                </button>

                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setPreviewZoom(100)}
                  title="Reset Zoom"
                >
                  <Maximize2 size={16} />
                </button>

                <div style={{ height: '18px', width: '1px', backgroundColor: 'var(--border-default)', margin: '0 4px' }} />

                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => handleRotateCw(activePreviewPage.id)}
                  title="Rotate Clockwise"
                >
                  <RotateCw size={16} />
                </button>

                <button
                  type="button"
                  className="icon-btn"
                  disabled={previewPageIndex === 0}
                  onClick={() => setPreviewPageIndex((idx) => (idx !== null ? Math.max(0, idx - 1) : 0))}
                  title="Previous Page (Left Arrow)"
                >
                  <ChevronLeft size={16} />
                </button>

                <button
                  type="button"
                  className="icon-btn"
                  disabled={previewPageIndex === pages.length - 1}
                  onClick={() => setPreviewPageIndex((idx) => (idx !== null ? Math.min(pages.length - 1, idx + 1) : 0))}
                  title="Next Page (Right Arrow)"
                >
                  <ChevronRight size={16} />
                </button>

                <div style={{ height: '18px', width: '1px', backgroundColor: 'var(--border-default)', margin: '0 4px' }} />

                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setPreviewPageIndex(null)}
                  aria-label="Close preview"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Viewer Canvas */}
            <div
              style={{
                flex: 1,
                padding: 'var(--space-6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#0f172a',
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  width: `${(previewZoom / 100) * 440}px`,
                  minHeight: `${(previewZoom / 100) * 620}px`,
                  backgroundColor: '#ffffff',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  transform: `rotate(${activePreviewPage.rotation}deg)`,
                  transition: 'transform 0.2s ease, width 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {activePreviewPage.thumbnailDataUrl ? (
                  <img
                    src={activePreviewPage.thumbnailDataUrl}
                    alt={`Page ${previewPageIndex + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ color: '#64748b' }}>Page {previewPageIndex + 1}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
